import isEqual from 'lodash/isEqual'
import { useState, useCallback, useMemo } from 'react'
// @mui
import Card from '@mui/material/Card'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import { alpha, useTheme } from '@mui/material/styles'

// routes
import { paths } from 'src/routes/paths'
// types
import { IUserTableFilterValue } from 'src/types/user'
// components
import Iconify from 'src/components/iconify'
import { useSettingsContext } from 'src/components/settings'
import Datagrid from 'src/components/datagrid'

import { Helmet } from 'react-helmet-async'
import useUsers from 'src/service/users.service'
import axiosInstance from 'src/utils/axios'
import { useSnackbar } from 'notistack'
import { useBoolean } from 'src/hooks/use-boolean'
import UserTableRow from './table-user/user-table-row'
import UserTableToolbar from './table-user/user-table-toolbar'
import UserTableFiltersResult from './table-user/user-table-filters-result'
import UserCreateEditForm from './user-create-edit-view'

export const USER_STATUS_OPTIONS = [
  { value: 'true', label: 'Ativos' },
  { value: 'false', label: 'Desativados' },
]

const _roles = [
  { value: 'admin', label: 'Admin' },
  { value: 'user', label: 'Vendedor' },
  { value: 'franchisee', label: 'Franqueado' },
]

const TABLE_HEAD = [
  { id: 'name', label: 'Vendedor' },
  { id: 'email', label: 'Email', width: 200 },
  { id: 'document', label: 'CPF', width: 160 },
  { id: 'allowedPayments', label: 'Pagamentos', width: 180 },
  { id: 'role', label: 'Tipo', width: 120 },
  { id: 'status', label: 'Status', width: 90 },
  { id: 'createdAt', label: 'Criado em', width: 140 },
  { id: '', width: 60 },
]

const defaultPagination = {
  page: 1,
  perPage: 10,
  sort: {
    createdAt: 'desc',
  },
}
const defaultFilters = {
  search: '',
  role: [],
  active: 'all',
}

export default function UserListView() {
  const theme = useTheme()
  const [pagination, setPagination] = useState(defaultPagination)
  const [filters, setFilters] = useState(defaultFilters)

  const { isLoading, isFetching, data, refetch } = useUsers(pagination, filters)

  const settings = useSettingsContext()

  const { enqueueSnackbar } = useSnackbar()

  const quickEdit = useBoolean()

  const canReset = !isEqual(defaultFilters, filters)

  const notFound = (!data?.data.length && canReset) || !data?.data.length

  const kpis = useMemo(() => {
    const users = data?.data || []
    return {
      total: data?.total || 0,
      active: users.filter((u: any) => u.active).length,
      inactive: users.filter((u: any) => !u.active).length,
    }
  }, [data])

  const handleFilters = useCallback((name: string, value: IUserTableFilterValue) => {
    setFilters((prevState) => ({
      ...prevState,
      [name]: value,
    }))
  }, [])

  const handleDeleteRow = useCallback(
    async (id: string) => {
      try {
        await axiosInstance.delete(`/user/${id}`)
        enqueueSnackbar('Vendedor removido com sucesso!')
        refetch()
      } catch (error) {
        enqueueSnackbar(error?.message || 'Erro ao remover vendedor!', { variant: 'error' })
      }
    },
    [enqueueSnackbar, refetch],
  )

  const handleResetFilters = useCallback(() => {
    setFilters(defaultFilters)
  }, [])

  const kpiCards = [
    { label: 'Total', value: kpis.total, icon: 'solar:users-group-rounded-bold', color: theme.palette.primary.main },
    { label: 'Ativos', value: kpis.active, icon: 'solar:check-circle-bold', color: theme.palette.success.main },
    { label: 'Inativos', value: kpis.inactive, icon: 'solar:close-circle-bold', color: theme.palette.warning.main },
  ]

  return (
    <>
      <Helmet>
        <title>Vendedores</title>
      </Helmet>

      <Container maxWidth={settings.themeStretch ? false : 'lg'}>
        {/* Header */}
        <Box
          sx={{
            mb: 3,
            p: { xs: 2, sm: 2.5 },
            borderRadius: 2,
            color: 'white',
            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ position: 'absolute', top: -40, right: -40, width: 120, height: 120, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.08)' }} />
          <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ position: 'relative', zIndex: 1 }}>
            <Stack direction='row' alignItems='center' spacing={1.5}>
              <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Iconify icon='solar:users-group-rounded-bold' width={20} />
              </Box>
              <Box>
                <Typography fontWeight='bold' sx={{ fontSize: { xs: '1.1rem', sm: '1.4rem' }, lineHeight: 1.2 }}>
                  Vendedores
                </Typography>
                <Typography sx={{ opacity: 0.7, fontSize: '0.75rem' }}>
                  Gerencie os vendedores do sistema
                </Typography>
              </Box>
            </Stack>

            <Button
              onClick={() => quickEdit.onTrue()}
              variant='contained'
              size='small'
              startIcon={<Iconify icon='mingcute:add-line' width={18} />}
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontWeight: 600,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                px: { xs: 1.5, sm: 2 },
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                whiteSpace: 'nowrap',
              }}
            >
              Novo vendedor
            </Button>
          </Stack>
        </Box>

        {/* KPI Cards */}
        <Box
          sx={{
            mb: 3,
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(3, 1fr)', sm: 'repeat(3, 1fr)' },
            gap: { xs: 1, sm: 2 },
          }}
        >
          {kpiCards.map((kpi) => (
            <Card
              key={kpi.label}
              sx={{
                p: { xs: 1.5, sm: 2 },
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'center', sm: 'center' },
                gap: { xs: 0.5, sm: 1.5 },
                textAlign: { xs: 'center', sm: 'left' },
              }}
            >
              <Box
                sx={{
                  width: { xs: 32, sm: 40 },
                  height: { xs: 32, sm: 40 },
                  borderRadius: 1.5,
                  bgcolor: alpha(kpi.color, 0.12),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Iconify icon={kpi.icon} width={{ xs: 18, sm: 22 }} sx={{ color: kpi.color }} />
              </Box>
              <Box>
                <Typography fontWeight={700} lineHeight={1.2} sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>
                  {kpi.value}
                </Typography>
                <Typography variant='caption' color='text.secondary' fontWeight={500} sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}>
                  {kpi.label}
                </Typography>
              </Box>
            </Card>
          ))}
        </Box>

        {/* Table Card */}
        <Card sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          {/* Status toggle chips */}
          <Stack direction='row' spacing={1} sx={{ px: { xs: 1.5, sm: 2.5 }, pt: { xs: 1.5, sm: 2.5 }, pb: 0, flexWrap: 'wrap', gap: 0.5 }}>
            {[
              { value: 'all', label: 'Todos', count: kpis.total },
              { value: 'true', label: 'Ativos', count: kpis.active },
              { value: 'false', label: 'Inativos', count: kpis.inactive },
            ].map((opt) => (
              <Chip
                key={opt.value}
                label={`${opt.label} (${opt.count})`}
                variant={filters.active === opt.value ? 'filled' : 'outlined'}
                color={filters.active === opt.value ? 'primary' : 'default'}
                onClick={() => handleFilters('active', opt.value)}
                size='small'
                sx={{ fontWeight: 600, fontSize: '0.75rem' }}
              />
            ))}
          </Stack>

          <UserTableToolbar filters={filters} onFilters={handleFilters} roleOptions={_roles} />

          {canReset && (
            <UserTableFiltersResult
              filters={filters}
              onFilters={handleFilters}
              onResetFilters={handleResetFilters}
              results={data?.data.length || 0}
              sx={{ p: 2.5, pt: 0 }}
            />
          )}

          <Datagrid
            data={data?.data || []}
            notFound={notFound}
            noSelection
            isLoading={isLoading || isFetching}
            headLabel={TABLE_HEAD}
            refetch={refetch}
            total={data?.total || 0}
            setPagination={setPagination}
            pagination={pagination}
            ItemContent={UserTableRow}
            handleDeleteRow={handleDeleteRow}
            rowsPerPageOptions={[10, 25, 50]}
            defaultDense
          />
        </Card>
      </Container>

      <UserCreateEditForm
        onRefleshTable={() => refetch()}
        open={quickEdit.value}
        onClose={quickEdit.onFalse}
      />
    </>
  )
}
